import { StoreState, ExchangeRate, Analytics, GenericTotal, LineItem } from 'app/state/types'
import { priceFromPriceObj } from '@ynap/price-utils'
import { SaleOrderFlag, TaxAndDutyFlag } from 'app/types/analytics.types'
import { Amount, PaymentInstruction, TaxByTaxCategory } from 'app/types/common-types'

interface State extends StoreState {
    analytics: Analytics
    basePrice: number
    basePriceGBP: number
    genericTotal?: GenericTotal[]
    lineItems?: LineItem[]
    itemSubtotal: number
    orderId: number
    paymentInstruction: PaymentInstruction[] | undefined
    totalSalesTax: Amount | undefined
    transactionTotal: number
    totals: number
    totalTaxByTaxCategory: TaxByTaxCategory[] | undefined
    userStatus: string
    useIdentifier: boolean
    x_exchangeRate: ExchangeRate | undefined
    x_taxType: string
}

export default function ({
    x_taxType,
    totalTaxByTaxCategory,
    paymentInstruction = [],
    totalSalesTax,
    lineItems = [],
    totals,
    orderId,
    genericTotal,
    basePrice,
    basePriceGBP,
    itemSubtotal,
    transactionTotal,
    analytics,
    locale,
    x_exchangeRate,
    userStatus,
    useIdentifier = false,
}: State) {
    const numSaleItems = lineItems.filter((each) => each.onSale)
    const numPromoItems = lineItems.filter((each) => each.onPromo)
    let saleOrderFlag
    if (!numSaleItems.length && !numPromoItems.length) {
        saleOrderFlag = SaleOrderFlag.FullPriceOrder
    } else if (numSaleItems.length === lineItems.length && !numPromoItems.length) {
        saleOrderFlag = SaleOrderFlag.SaleOrder
    } else if (numPromoItems.length === lineItems.length && !numSaleItems.length) {
        saleOrderFlag = SaleOrderFlag.PromoOrder
    } else {
        saleOrderFlag = SaleOrderFlag.PartialSaleOrPromoOrder
    }

    const taxByTaxCategoryOfAllLineItems = (lineItems || []).reduce<TaxByTaxCategory[]>((acc, cur) => {
        if (Array.isArray(cur.taxByTaxCategory)) {
            return [...acc, ...cur.taxByTaxCategory]
        }
        return acc
    }, [])

    const rawTaxAndDuty = getTaxAndDuty(x_taxType, taxByTaxCategoryOfAllLineItems)

    const rawTotalSalesTax = priceFromPriceObj(totalSalesTax) || 0

    const localCustomsFee = getLocalCustomsFee(totalTaxByTaxCategory)

    const storeCredit = getStoreCredit(paymentInstruction)

    const taxAndDutyFlag = getTaxAndDutyFlag(x_taxType, rawTotalSalesTax)

    const total = transactionTotal - storeCredit

    return {
        lineItems,
        totals,
        orderId,
        genericTotal,
        basePrice,
        basePriceGBP,
        itemSubtotal,
        transactionTotal: total,
        saleOrderFlag,
        taxAndDuty: rawTaxAndDuty + rawTotalSalesTax,
        localCustomsFee,
        storeCredit: storeCredit > 0 ? -storeCredit : storeCredit,
        taxAndDutyFlag,
        analytics,
        locale,
        x_exchangeRate,
        userStatus,
        useIdentifier,
    }
}

export function getTaxAndDuty(taxType, taxes: TaxByTaxCategory[] = []) {
    if (taxType === 'DDP_INCLUDED') return 0
    return [...taxes]
        .filter((each) => each.taxCategoryCode === 'ITEM_VAT' || each.taxCategoryCode === 'ITEM_DUTIES')
        .reduce((acc, cur) => {
            return priceFromPriceObj(cur.value) + acc
        }, 0)
}

export function getLocalCustomsFee(taxes: TaxByTaxCategory[] = []) {
    const brokerageFee = taxes.find((i) => i.taxCategoryCode === 'BROKERAGE_FEE')
    return brokerageFee ? priceFromPriceObj(brokerageFee.value) : 0
}

export function getStoreCredit(paymentInstructions: PaymentInstruction[] = []) {
    const price = paymentInstructions.find((payment) => payment.payMethodId === 'STORE_CREDIT')
    if (price) {
        return priceFromPriceObj(price.piAmount)
    }
    return 0
}

export function getTaxAndDutyFlag(taxType: string, rawTotalSalesTax = 0) {
    if (taxType === 'DDP_INCLUDED') {
        return TaxAndDutyFlag.TaxAndDutyIncluded
    } else if (taxType === 'DDP') {
        return TaxAndDutyFlag.TaxAndDutyAdded
    } else if ((taxType === 'US_LOCAL_SALES_TAXES' || taxType === 'US_LOCAL_ESTIMATED_TAXES') && rawTotalSalesTax > 0) {
        return TaxAndDutyFlag.SalesTaxAdded
    } else if (taxType === 'TAX_NOT_FOUND') {
        return TaxAndDutyFlag.NotAvailable
    } else {
        return TaxAndDutyFlag.NoAdditionalTax
    }
}
